import { FC } from 'react';

import { Body, Button, Subhead, Tag, useRebrand } from '@growtherapy/sprout-ui';

import { RemoteParticipant, parseIdentity } from '../../video-sdk';
import { ClientActions } from './ClientActions';
import { ClientActionsMenu } from './ClientActionsMenu';

export type ClientsInSessionActionsProps = {
  onRemove: () => void;
  isConversationLoading?: boolean;
  loading?: boolean;
  clientName: string;
  unreadMessagesCount?: number;
  patientShortId?: string;
  onChat?: () => void;
  onOpenClientInformationDrawer: () => void;
};

export function ClientsInSessionActions({
  onRemove,
  loading,
  clientName,
  patientShortId,
  unreadMessagesCount,
  isConversationLoading,
  onChat,
  onOpenClientInformationDrawer,
}: ClientsInSessionActionsProps) {
  return (
    <ClientActions
      clientName={clientName}
      actions={
        <div className='flex gap-2 shrink-0'>
          <Button
            data-testid={`client-actions.remove:${clientName}`}
            loading={loading}
            use='destructive'
            onClick={onRemove}
          >
            Remove
          </Button>
          <ClientActionsMenu
            visitorName={clientName}
            loading={loading}
            isConversationLoading={isConversationLoading}
            unreadMessagesCount={unreadMessagesCount}
            onChat={onChat}
            patientShortId={patientShortId}
            entryPoint='overflow_menu'
            onOpenClientInformationDrawer={onOpenClientInformationDrawer}
          />
        </div>
      }
    />
  );
}

export function ClientsInSession({
  clientActionsComponent: ClientActionsComponent,
  participants,
}: {
  clientActionsComponent: FC<{ clientName: string; participantSid: string; visitorUuid: string }>;
  participants: RemoteParticipant[];
}) {
  const isRebrand = useRebrand();
  return (
    <>
      <div className='flex items-center gap-2'>
        <Subhead.Small
          as='h3'
          className='font-medium text-medium rebrand:text-text-inverse'
          aria-label='Clients in session'
        >
          In session
        </Subhead.Small>
        <Tag use={isRebrand ? 'lilac' : 'mauve'} data-testid='in-session.count' className='rebrand:ring-0'>
          {participants.length}
        </Tag>
      </div>
      {participants.length ? (
        participants.map((participant) => {
          const { id } = parseIdentity(participant.identity);
          return (
            <ClientActionsComponent
              key={participant.sid}
              clientName={participant.displayName}
              participantSid={participant.sid}
              visitorUuid={id}
            />
          );
        })
      ) : (
        <Body.Default className='rebrand:text-text-inverse'>
          Your clients will appear here as soon as they join the session.
        </Body.Default>
      )}
    </>
  );
}
